import m from 'mithril';
import MessageProvider from '../../../MessageProvider';
import dayjs from 'dayjs'
import { NightlyRate, RoomType, RoomTypeBookableOption, TemporaryRoomTypeSelection } from '../../../Models';
import './RoomTypeBookableOptionComponent.css'

interface Attrs {
    numOfRooms: number,
    totalAvailableRoomCount: number,
    hasBookableRooms: boolean,
    messageProvider: MessageProvider;
    roomType: RoomType;
    roomTypeBookableOption: RoomTypeBookableOption;
    numberOfExtraBedsAllowed: number;    
    onOptionSelected: (item: TemporaryRoomTypeSelection) => void;
}

export default function RoomTypeBookableOptionComponent(): m.Component<Attrs> {
    let showingDetails: boolean = false;  
    let selectedNumberOfExtraBeds = 0;

    return {
        view(vnode) {
            const {numOfRooms, totalAvailableRoomCount, hasBookableRooms, messageProvider, roomType, roomTypeBookableOption, numberOfExtraBedsAllowed, 
                onOptionSelected} = vnode.attrs;
            const bookableOption = roomTypeBookableOption;
            const numberOfNights = bookableOption.nightlyRates.length;      

            const averageExtraBedRate = bookableOption.nightlyRates.reduce(
                (aggregate: number, nightlyRate: NightlyRate) => 
                    aggregate + ((nightlyRate.extraBedRate != null)? nightlyRate.extraBedRate.numericValue: 0), 0) 
                    / numberOfNights;

            const subtotalAmountReducer = (aggregate: number, nightlyRate: NightlyRate) => {
                let totalExtraBedAmount = (nightlyRate.extraBedRate != null)? 
                    selectedNumberOfExtraBeds * nightlyRate.extraBedRate.numericValue : 0;
                return aggregate + nightlyRate.baseRoomRate.numericValue + totalExtraBedAmount;
            }
            const subtotalAmount = bookableOption.nightlyRates.reduce(subtotalAmountReducer, 0)
            const averageRatePerNight = subtotalAmount / numberOfNights;

            const discountAmountReducer = (aggregate: number, nightlyRate: NightlyRate) => {                    
                return aggregate + ((nightlyRate.discountApplied)? nightlyRate.discountApplied?.amount.numericValue : 0);            
            }
            const subtotalDiscountAmount: number|undefined = (bookableOption.isPromoAvailable)?
                bookableOption.nightlyRates.reduce(discountAmountReducer, 0) : undefined;
            const subtotalWithoutDiscountAmount: number|undefined = (subtotalDiscountAmount)? 
                subtotalAmount + subtotalDiscountAmount : undefined;   
                        
            const maxOccupancy = roomType.standardOccupancy + selectedNumberOfExtraBeds;

            const options: m.Vnode<any, any>[] = [];

            for (let i = 1; i <= numberOfExtraBedsAllowed; i++) {
                options.push(
                    m('option', {
                            value: i,
                            onclick: () => {
                                selectedNumberOfExtraBeds = i;
                            }
                        }, `${i}`
                    )
                );
            }

            const bedType = roomType.beds.map((bed, index) => {
                let bedType = ""
                let bedUnitLabel = bed.quantity>1? " Beds" : " Bed";
                if(index < roomType.beds.length -1){
                    bedType += bed.quantity + " " + bed.type + bedUnitLabel + ", ";
                }else{
                    bedType += bed.quantity + " " + bed.type + bedUnitLabel;
                }
                return bedType;
            }) 

            return m("div", [
                m("div", { class: "p-3 border-top" }, [
                    m("div.d-flex", [
                       m("div.mr-auto font-weight-bold text-secondary",  bookableOption.title),
                       m("div.text-right", [
                            m("div", [
                                (subtotalWithoutDiscountAmount)?
                                m("small.room-price-overriden mr-2", 
                                    new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(subtotalWithoutDiscountAmount)
                                ): null,
                                m("span.total-rate", "MMK " + new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(subtotalAmount)),
                            ]),
                            (subtotalWithoutDiscountAmount)? m("div.badge badge-danger", "Save " +  new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(subtotalWithoutDiscountAmount - subtotalAmount)):null
                        ])
                    ]),
                    m("div.row", [
                        m("div.col-8", [                     
                            (roomType.beds.length > 0)?
                                m("div.text-muted pt-2", m("small", m("i.bi bi-usb-mini pr-2"), bedType))
                            :null,

                            m("div.mt-1 d-flex text-muted pb-1", 
                                m("div.pr-2", m("small", m("i.bi bi-people"))),
                                m("div", 
                                    m("small", {}, [
                                        messageProvider.interpolate("number_of_adults", {count:maxOccupancy}),
                                        (messageProvider.getCurrentLang() == "en")?", ":"၊ ",
                                        (roomType.maxChildAge > 0)? messageProvider.interpolate("number_of_child", {count: 1}) + " (" + messageProvider.interpolate("above_years_of_age", {years:roomType.maxChildAge}) + ")":null
                                    ])
                                )
                            ),

                            (numberOfExtraBedsAllowed > 0)?
                            [
                                m("div.d-flex align-items-center pt-2", {
                                    onclick: () => {
                                        if(selectedNumberOfExtraBeds == 0) {
                                            selectedNumberOfExtraBeds = 1;
                                        } else {
                                            selectedNumberOfExtraBeds = 0;
                                        }
                                    }
                                }, [
                                    m("div.text-primary", m("i", {class: (selectedNumberOfExtraBeds == 0)? "bi bi-square": "bi bi-check-square-fill"})),
                                    m("div.px-2", [
                                        m("small", messageProvider.get("add_extra_bed") + " (MMK " + new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(averageExtraBedRate) + " per night)")
                                    ]),
                                    
                                ]),
                                (numberOfExtraBedsAllowed > 1 && selectedNumberOfExtraBeds > 0)?[
                                    m("select.custom-select-tag ml-4 mt-1", options)
                                ]:null,
                            ]:null,

                            (numberOfNights > 1)?
                            m("div.py-2",
                                m("small", {
                                    class: "text-info",
                                    onclick: function (event) {
                                        event.preventDefault();
                                        showingDetails = !showingDetails;
                                    }
                                }, [
                                    messageProvider.get("details") + " ",
                                    (showingDetails) ?
                                    m("i.bi bi-chevron-up") :
                                    m("i.bi bi-chevron-down")
                                ])
                            ):null                            
                        ]),
                        m("div.col-4 text-right", [
                            m("div", {}, [
                                m("small.text-muted mt-0", {}, 
                                (numberOfNights == 1)? "1 " + messageProvider.get("night") : numberOfNights + " " + messageProvider.get("nights") + " x "  
                                + new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(averageRatePerNight) + " Avg")
                            ]),
                            m("div", { class: "mt-2" }, [
                                (numOfRooms <= totalAvailableRoomCount && hasBookableRooms)?
                                m("button", {
                                    class: "btn button-accent btn-sm mmd-ripple raised px-4 py-2 rounded",
                                    onclick: function (event) {
                                        event.preventDefault();

                                        let roomSelection = new TemporaryRoomTypeSelection(
                                            roomType, bookableOption, selectedNumberOfExtraBeds, maxOccupancy);
                                        onOptionSelected(roomSelection);    
                                    }
                                }, messageProvider.get("select")):
                                m("button", {
                                    class: "btn button-accent text-white px-4 py-2 rounded",
                                    disabled: true
                                }, messageProvider.get("select"))
                            ])
                        ])
                    ]),

                    (showingDetails) ?
                    m("div.mt-3", 
                        m("table", { class: "table table-striped table-border" }, [
                            m("tr", [
                                m("th", {}, m("small.font-weight-bold", "Date")),
                                (selectedNumberOfExtraBeds > 0)?
                                m("th", m("small.font-weight-bold", "Extra Bed")):null,

                                m("th", { class: "text-right" }, m("small.font-weight-bold", "Price"))
                            ]),
                            bookableOption.nightlyRates.map(function (nightlyRate) {
                                const totalExtraBedAmount = (nightlyRate.extraBedRate != null)? 
                                    nightlyRate.extraBedRate.numericValue * selectedNumberOfExtraBeds : 0;

                                return m("tr", [
                                    m("td",
                                        m("small", dayjs(nightlyRate.date).format("MMM-DD"))),

                                    (selectedNumberOfExtraBeds > 0)?
                                    m("td", 
                                        m("small", "1 Included"))                                    
                                    :null,    

                                    m("td.text-right",
                                        m("small", new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(
                                            nightlyRate.baseRoomRate.numericValue + 
                                            (selectedNumberOfExtraBeds * totalExtraBedAmount))
                                        )
                                    )
                                ])
                            })
                        ])
                    )
                    : ""
                ]),
            ])
        }
    }
}